
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ArrowDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface ExportDataButtonProps {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
}

const ExportDataButton = ({ variant = "outline" }: ExportDataButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleExport = async () => {
    if (!user) {
      toast({
        title: "Erro ao exportar dados",
        description: "Você precisa estar autenticado para exportar seus dados.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Buscar dados do usuário de várias tabelas
      const userId = user.id;
      
      // Buscar perfil do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (profileError) throw profileError;
      
      // Buscar registros de emoções
      const { data: emotionData, error: emotionError } = await supabase
        .from('emotion_records')
        .select('*')
        .eq('user_id', userId)
        .order('recorded_at', { ascending: false });
      
      if (emotionError) throw emotionError;
      
      // Buscar registros de desempenho no trabalho
      const { data: workPerformanceData, error: workPerformanceError } = await supabase
        .from('work_performance')
        .select('*')
        .eq('user_id', userId)
        .order('recorded_at', { ascending: false });
      
      if (workPerformanceError) throw workPerformanceError;
      
      // Buscar resultados de testes de personalidade (corrigido para young_schema_results)
      const { data: personalityData, error: personalityError } = await supabase
        .from('young_schema_results')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (personalityError) throw personalityError;
      
      // Buscar sessões de chat
      const { data: chatSessionsData, error: chatSessionsError } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (chatSessionsError) throw chatSessionsError;
      
      // Criar objeto de dados completo
      const userData = {
        profile: profileData,
        emotions: emotionData,
        workPerformance: workPerformanceData,
        personality: personalityData,
        chatSessions: chatSessionsData,
        exportedAt: new Date().toISOString(),
      };
      
      // Converter para JSON
      const dataStr = JSON.stringify(userData, null, 2);
      
      // Criar um blob e um link de download
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `vive-calme-dados-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast({
        title: "Dados exportados com sucesso!",
        description: "Seus dados foram exportados para um arquivo JSON.",
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Erro ao exportar dados",
        description: "Tente novamente mais tarde ou contate o suporte.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button 
      variant={variant} 
      onClick={handleExport} 
      disabled={isLoading}
      className="gap-2"
    >
      <ArrowDown size={16} />
      {isLoading ? "Gerando relatório..." : "Exportar meus dados"}
    </Button>
  );
};

export default ExportDataButton;
